import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Copy } from "lucide-react";
import { useToast } from "@/shared/hooks/use-toast";
import { useEffect, useState } from "react";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { v4 } from "uuid";
import { useNavigate } from "react-router";
import { useVbsOfferCreateFromOldMutation } from "@/services/backend/vbs/offers/api/offer-api";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { BranchSelectFrom } from "@/services/backend/branch/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";

/**
 * DuplicateOfferButton creates a new editable offer from a committed
 * (immutable) one, carrying over its data. It is the way to "update" a
 * festgeschriebenes Angebot. Rendered as a popover so it fits into the
 * document page action bar.
 */
export function DuplicateOfferButton({
  processId,
  offerId,
}: {
  processId: string | null;
  offerId: string;
}) {
  const [create, { isLoading, error, reset }] =
    useVbsOfferCreateFromOldMutation();
  const { data: process } = useProcessesProcessShowQuery(
    { id: processId ?? NullUUID },
    { skip: !processId },
  );

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  const disabled =
    isLoading ||
    !process ||
    !process.customerId ||
    branchId === "" ||
    serviceObjectId === "";

  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      toast({ ...parseRTKQueryError(error), variant: "destructive" });
      reset();
    }
  }, [error, toast, reset]);

  const onClick = () => {
    if (disabled) {
      return;
    }

    const id = v4();
    create({
      oldOfferId: offerId,
      id,
      processId: process.id,
      companyId: process.companyId,
      branchId,
      customerId: process.customerId!,
      serviceObjectId,
    })
      .unwrap()
      .then(() => {
        navigate(`../${id}`);
        reset();
      });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline">
          <Copy className="mr-2 h-5 w-5" />
          <span>{t("Aktualisieren")}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-80 flex-col gap-2">
        <div className="text-muted-foreground text-xs">
          {t(
            "Ein Klick auf Aktualisieren erstellt eine neues Angebot und übernimmt die Daten des aktuellen.",
          )}
        </div>
        {branches.length !== 1 && (
          <BranchSelectFrom
            branchIds={branches}
            value={branchId}
            onValueChange={setBranchId}
          />
        )}
        {serviceObjects.length !== 1 && (
          <ServiceObjectSelectFrom
            serviceObjectIds={serviceObjects}
            customerId={process?.customerId}
            value={serviceObjectId}
            onValueChange={setServiceObjectId}
          />
        )}
        <Button
          onClick={onClick}
          disabled={disabled}
          className="w-full justify-start"
        >
          <Copy className="mr-4 h-5 w-5" />
          <span>{t("Aktualisieren")}</span>
        </Button>
      </PopoverContent>
    </Popover>
  );
}
